import dayjs from "dayjs";
import { DOMAIN_REGEX, EMAIL_REGEX, PHONE_REGEX } from "../../../../../../../../../../constants/regexConstants";
import { formatSubdomainInputValue } from "../../../../../../../../../../utils/inputUtils";
import { IAgentPersonalInformation } from "../../../../../../../../agentDetail.model";
import { AgentDetailFormFieldConfig } from "../agentDetailFormField/agentDetailFormField.types";

export interface PersonalFormFieldConfig extends AgentDetailFormFieldConfig {
    field: keyof IAgentPersonalInformation;
    addonField?: keyof IAgentPersonalInformation;
}

export const PERSONAL_FORM_FIELDS: PersonalFormFieldConfig[] = [
    { field: "firstName", label: "First Name", type: "text", placeholder: "Enter First Name" },
    { field: "lastName", label: "Last Name", type: "text", placeholder: "Enter Last Name" },
    {
        field: "email",
        label: "Email",
        type: "email",
        placeholder: "Enter Email",
        validate: (v, original) => {
            if(original?.trim() && !v?.trim()) return "Email is required";
            if (!v?.trim()) return null;
            return EMAIL_REGEX.test(v) ? null : "Please enter a valid email";
        },
    },
    {
        field: "subDomain",
        label: "Website",
        type: "website",
        placeholder: "Enter Website",
        addonField: "domain",
        filter: formatSubdomainInputValue,
        validate: (v, original) => {
            if (original && !v) return "Website is required";
            if (!v) return null;
            return DOMAIN_REGEX.test(v) ? null : "Domain may only contain letters and hyphens";
        },
    },
    {
        field: "primaryPhoneNumber",
        label: "Phone",
        type: "tel",
        placeholder: "Enter Phone",
        validate: (v) => {
            if (!v) return null;
            return PHONE_REGEX.test(v) ? null : "Please enter a valid phone number";
        },
    },
    {
        field: "secondaryPhoneNumber",
        label: "Mobile Phone",
        type: "tel",
        placeholder: "Enter Mobile Phone",
        validate: (v) => {
            if (!v) return null;
            return PHONE_REGEX.test(v) ? null : "Please enter a valid phone number";
        },
    },
    {
        field: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
        placeholder: "MM/DD/YYYY",
        validate: (v) => {
            if (!v) return null;
            return dayjs(v).isAfter(dayjs(), "day")
                ? "Date of birth cannot be in the future"
                : null;
        },
    },
    { field: "placeOfBirth", label: "Place of Birth", type: "select", placeholder: "Select State" },
    { field: "userTimeZone", label: "Time Zone", type: "text", placeholder: "Time Zone" },
    {
        field: "ssnNumber",
        label: "SSN",
        type: "text",
        placeholder: "Enter SSN",
        filter: (v) => v.replace(/\D/g, "").slice(0, 12),
        validate: (v, original) => {
            if (original && !v) return "SSN is required";
            if (!v) return null;
            if (/\D/.test(v)) return null;
            return /^\d{9,12}$/.test(v) ? null : "SSN must be 9-12 digits";
        },
    },
    { field: "npnNumber", label: "NPN", type: "text", placeholder: "Enter NPN" },
    {
        field: "addressLine1",
        label: "Residential Street Address",
        type: "text",
        placeholder: "Enter Street Address",
    },
    {
        field: "addressLine2",
        label: "Residential Street Address 2",
        type: "text",
        placeholder: "Enter Street Address 2",
    },
    { field: "city", label: "Residential City", type: "text", placeholder: "Enter City" },
    { field: "state", label: "Residential State", type: "select", placeholder: "Select State" },
    {
        field: "zipCode",
        label: "Residential Zipcode",
        type: "text",
        placeholder: "Enter Zipcode",
        filter: (v) => v.replace(/\D/g, "").slice(0, 7),
        validate: (v) => {
            if (!v) return null;
            return /^\d{1,7}$/.test(v) ? null : "Zip code must be at most 7 digits";
        },
    },
    {
        field: "driversLicenseNumber",
        label: "Drivers License",
        type: "text",
        placeholder: "Enter Drivers License",
    },
    {
        field: "driversLicenseState",
        label: "Driver License State",
        type: "select",
        placeholder: "Select State",
    },
    { field: "unitNumber", label: "Unit Number", type: "text", placeholder: "Enter Unit Number" },
];

export const hasPersonalInfoErrors = (
    info: IAgentPersonalInformation | null,
    original?: IAgentPersonalInformation | null
): boolean => {
    if (!info) return false;
    return PERSONAL_FORM_FIELDS.some(({ field, validate }) => {
        if (!validate) return false;
        return (
            validate(
                info[field] as string | null,
                original?.[field] as string | null
            ) !== null
        );
    });
};
